import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import {
  getOfferLetters,
  createOfferLetter,
  generateOfferLetterPDF,
  downloadOfferAnnexure,
  sendOfferAnnexure,
  sendOfferLetter,
  bulkSendOfferLetters,
  bulkDownloadOfferLetters,
  downloadOfferLetterTemplate,
  bulkUploadOfferLetters,
  getOfferLetterTemplates,
  updateOfferAnnexureEffectiveDate,
  bulkUpdateOfferAnnexureEffectiveDate,
  acceptOfferLetter
} from '../api';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Mail, Plus, FileText, Download, Send, Users, CheckCircle,
  Clock, XCircle, Upload, FileSpreadsheet, Trash2, Eye, Settings2,
  Paperclip, FileSignature, PenLine, History, Bell, RefreshCw, Search, CalendarClock
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import ZohoSignDialog from '../components/ZohoSignDialog';
import ZohoSignBulkDialog from '../components/ZohoSignBulkDialog';
import AnnexureHistoryDialog from '../components/AnnexureHistoryDialog';
import ZohoSignRemindStaleDialog from '../components/ZohoSignRemindStaleDialog';
import ZohoSignBulkRefreshDialog from '../components/ZohoSignBulkRefreshDialog';
import AutoRefreshToggle from '../components/AutoRefreshToggle';
import { listStaleESignOffers, countInProgressESignOffers } from '../api';

const statusColors = {
  draft: 'bg-slate-100 text-slate-700',
  generated: 'bg-blue-100 text-blue-700',
  sent: 'bg-emerald-100 text-emerald-700',
  accepted: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700'
};

const statusIcons = {
  draft: Clock,
  generated: FileText,
  sent: Send,
  accepted: CheckCircle,
  rejected: XCircle
};

const OfferLetters = ({ user, setUser }) => {
  const [offers, setOffers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [bulkSendDialogOpen, setBulkSendDialogOpen] = useState(false);
  const [selectedOfferToSend, setSelectedOfferToSend] = useState(null);
  const [sendFormData, setSendFormData] = useState({ email: '', cc: '' });
  const [bulkSendCC, setBulkSendCC] = useState('');
  const [selectedOffers, setSelectedOffers] = useState([]);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewOffer, setPreviewOffer] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [esignDialogOpen, setEsignDialogOpen] = useState(false);
  const [esignOffer, setEsignOffer] = useState(null);
  const [esignBulkOpen, setEsignBulkOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyOffer, setHistoryOffer] = useState(null);
  const [historyInitialFilter, setHistoryInitialFilter] = useState('all');
  const [staleDialogOpen, setStaleDialogOpen] = useState(false);
  const [staleCount, setStaleCount] = useState(0);
  const [refreshDialogOpen, setRefreshDialogOpen] = useState(false);
  const [inProgressCount, setInProgressCount] = useState(0);
  const [annexDateDialogOpen, setAnnexDateDialogOpen] = useState(false);
  const [annexDateOffer, setAnnexDateOffer] = useState(null);
  const [annexDateValue, setAnnexDateValue] = useState('');
  const [annexDateSaving, setAnnexDateSaving] = useState(false);
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [acceptOffer, setAcceptOffer] = useState(null);
  const [acceptTrigger, setAcceptTrigger] = useState('now');
  const [acceptScheduledAt, setAcceptScheduledAt] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [bulkAnnexDialogOpen, setBulkAnnexDialogOpen] = useState(false);
  const [bulkAnnexValue, setBulkAnnexValue] = useState('');
  const [bulkAnnexSaving, setBulkAnnexSaving] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [statsCounts, setStatsCounts] = useState({ total: 0, draft: 0, generated: 0, sent: 0, accepted: 0 });
  const [formData, setFormData] = useState({
    template_id: '',
    honorific: 'Mr.',
    employee_id: '',
    candidate_name: '',
    candidate_email: '',
    designation: '',
    work_location: '',
    department: '',
    offer_date: new Date().toISOString().split('T')[0],
    start_date: '',
    annexure_effective_date: '',
    basic_da: 17500,
    hra: 8750,
    statutory_bonus: 1750,
    other_allowance: 7000,
    variable_amount: 120000,
    pf_deduction: 0,
    esic_deduction: 0,
    lwf_deduction: 0,
    professional_tax: 200,
    insurance_deduction: 400,
    tds_deduction: null
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCC, setUploadCC] = useState('');

  useEffect(() => {
    fetchTemplates();
    fetchStaleCount();
    fetchInProgressCount();
  }, []);

  // Refetch on search / page / pageSize changes (debounced 300ms for search typing)
  useEffect(() => {
    const handle = setTimeout(() => {
      fetchOffers({ search: searchQuery, page, pageSize });
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, page, pageSize]);

  // Reset page to 1 when search changes
  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const fetchStaleCount = async () => {
    try {
      const { data } = await listStaleESignOffers(24);
      setStaleCount(data.total_stale || 0);
    } catch {
      setStaleCount(0);
    }
  };

  const fetchInProgressCount = async () => {
    try {
      const { data } = await countInProgressESignOffers();
      setInProgressCount(data.count || 0);
    } catch {
      setInProgressCount(0);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await getOfferLetterTemplates();
      setTemplates(response.data);
      // Set default template
      const defaultTemplate = response.data.find(t => t.is_default);
      if (defaultTemplate) {
        setFormData(prev => ({ ...prev, template_id: defaultTemplate.id }));
      }
    } catch (error) {
      console.error('Failed to fetch templates');
    }
  };

  const fetchOffers = async (overrides = {}) => {
    try {
      const q = overrides.search !== undefined ? overrides.search : searchQuery;
      const p = overrides.page !== undefined ? overrides.page : page;
      const ps = overrides.pageSize !== undefined ? overrides.pageSize : pageSize;
      const params = { limit: ps, skip: (p - 1) * ps };
      if (q && q.trim()) params.search = q.trim();
      const response = await getOfferLetters(params);
      const data = response.data;
      if (Array.isArray(data)) {
        setOffers(data);
        setTotal(data.length);
        setStatsCounts({ total: data.length, draft: 0, generated: 0, sent: 0, accepted: 0 });
      } else {
        setOffers(data.offers || []);
        setTotal(data.total ?? 0);
        setStatsCounts(data.stats || { total: data.total ?? 0, draft: 0, generated: 0, sent: 0, accepted: 0 });
      }
    } catch (error) {
      toast.error('Failed to fetch offer letters');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createOfferLetter(formData);
      toast.success('Offer letter created successfully');
      setDialogOpen(false);
      resetForm();
      fetchOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create offer letter');
    }
  };

  const handleDownloadPDF = async (offerId, candidateName) => {
    try {
      const response = await generateOfferLetterPDF(offerId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Offer_Letter_${candidateName.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  // Annexure (standalone, designation-matched, personalised) — downloaded
  // and/or emailed separately from the offer letter for e-signing.
  const handleDownloadAnnexure = async (offerId, candidateName) => {
    try {
      const response = await downloadOfferAnnexure(offerId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Annexure_${(candidateName || 'candidate').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Annexure downloaded');
    } catch (error) {
      // The backend returns JSON for errors but axios delivers it as a Blob
      // (because we requested responseType: 'blob'). Extract the detail
      // robustly — handle Blob, string, and object payloads — so HR sees
      // the actual reason (e.g. "No annexure configured for designation
      // 'Team Lead' …") instead of the generic fallback.
      let detail = null;
      try {
        const data = error?.response?.data;
        if (data) {
          if (typeof data.text === 'function') {
            // Blob path
            const text = await data.text();
            try {
              const parsed = JSON.parse(text);
              detail = parsed?.detail || text;
            } catch {
              detail = text;
            }
          } else if (typeof data === 'string') {
            try { detail = JSON.parse(data)?.detail || data; }
            catch { detail = data; }
          } else if (typeof data === 'object') {
            detail = data?.detail || JSON.stringify(data);
          }
        }
      } catch (parseErr) {
        // eslint-disable-next-line no-console
        console.error('annexure download error parse failed:', parseErr);
      }
      if (!detail || typeof detail !== 'string') {
        const status = error?.response?.status;
        detail = status
          ? `Failed to download annexure (HTTP ${status}). Offer ID: ${offerId}`
          : `Failed to download annexure. Offer ID: ${offerId}`;
      }
      toast.error(detail, { duration: 8000 });
    }
  };

  const handleSendAnnexure = async (offer) => {
    if (!offer.candidate_email) {
      toast.error('Candidate email missing — cannot send annexure.');
      return;
    }
    if (!window.confirm(`Email the salary annexure to ${offer.candidate_email}?`)) return;
    try {
      await sendOfferAnnexure(offer.id, {});
      toast.success(`Annexure emailed to ${offer.candidate_email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send annexure');
    }
  };

  const openAnnexDateDialog = (offer) => {
    setAnnexDateOffer(offer);
    setAnnexDateValue(offer.annexure_effective_date || '');
    setAnnexDateDialogOpen(true);
  };

  const handleAcceptOffer = (offer) => {
    if (offer.status === 'accepted') {
      toast.info('Offer already accepted');
      return;
    }
    setAcceptOffer(offer);
    setAcceptTrigger('now');
    setAcceptScheduledAt(
      offer.start_date || offer.date_of_joining || offer.doj || ''
    );
    setAcceptDialogOpen(true);
  };

  const confirmAcceptOffer = async () => {
    if (!acceptOffer) return;
    setAccepting(true);
    try {
      const { data } = await acceptOfferLetter(acceptOffer.id, {
        bgv_trigger: acceptTrigger,
        bgv_scheduled_at: acceptTrigger === 'scheduled' ? acceptScheduledAt : null,
      });
      if (acceptTrigger === 'now' && data.bgv_case) {
        toast.success(
          `Offer accepted · BGV case ${data.bgv_case.case_number} created & invite emailed`,
          { duration: 5000 }
        );
      } else if (acceptTrigger === 'scheduled') {
        toast.success(
          `Offer accepted · BGV scheduled for ${data.bgv_scheduled_at}`,
          { duration: 5000 }
        );
      } else {
        toast.success('Offer accepted (BGV skipped)');
      }
      setAcceptDialogOpen(false);
      setAcceptOffer(null);
      fetchOffers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept offer');
    } finally {
      setAccepting(false);
    }
  };

  const handleSaveAnnexDate = async () => {
    if (!annexDateOffer) return;
    setAnnexDateSaving(true);
    try {
      await updateOfferAnnexureEffectiveDate(annexDateOffer.id, annexDateValue || null);
      toast.success(
        annexDateValue
          ? 'Annexure effective date updated'
          : 'Annexure effective date cleared (will default to DOJ)'
      );
      setAnnexDateDialogOpen(false);
      setAnnexDateOffer(null);
      setAnnexDateValue('');
      fetchOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update annexure effective date');
    } finally {
      setAnnexDateSaving(false);
    }
  };

  const handleBulkSaveAnnexDate = async () => {
    if (selectedOffers.length === 0) {
      toast.error('Please select at least one offer letter');
      return;
    }
    setBulkAnnexSaving(true);
    try {
      const res = await bulkUpdateOfferAnnexureEffectiveDate(
        selectedOffers,
        bulkAnnexValue || null
      );
      toast.success(
        bulkAnnexValue
          ? `Updated ${res.data?.modified ?? selectedOffers.length} offers with effective date ${bulkAnnexValue}`
          : `Cleared annexure effective date on ${res.data?.modified ?? selectedOffers.length} offers`
      );
      setBulkAnnexDialogOpen(false);
      setBulkAnnexValue('');
      setSelectedOffers([]);
      fetchOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk-update annexure effective date');
    } finally {
      setBulkAnnexSaving(false);
    }
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedOffers = React.useMemo(() => {
    if (!sortField) return offers;
    const arr = [...offers];
    arr.sort((a, b) => {
      const va = (a?.[sortField] || '').toString();
      const vb = (b?.[sortField] || '').toString();
      // Empty strings sort last regardless of direction
      if (!va && !vb) return 0;
      if (!va) return 1;
      if (!vb) return -1;
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [offers, sortField, sortDir]);

  const handlePreviewPDF = async (offer) => {
    setPreviewOffer(offer);
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const response = await generateOfferLetterPDF(offer.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (error) {
      toast.error('Failed to load PDF preview');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setPreviewOffer(null);
  };

  const handleSendOffer = async (offerId, email, cc = '') => {
    try {
      const ccList = cc ? cc.split(',').map(e => e.trim()).filter(e => e) : [];
      await sendOfferLetter(offerId, email, ccList);
      toast.success('Offer letter sent successfully');
      setSendDialogOpen(false);
      setSelectedOfferToSend(null);
      setSendFormData({ email: '', cc: '' });
      fetchOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send offer letter');
    }
  };

  const openSendDialog = (offer) => {
    setSelectedOfferToSend(offer);
    setSendFormData({ email: offer.candidate_email, cc: '' });
    setSendDialogOpen(true);
  };

  const handleBulkSend = async () => {
    if (selectedOffers.length === 0) {
      toast.error('Please select at least one offer letter');
      return;
    }
    try {
      const ccList = bulkSendCC ? bulkSendCC.split(',').map(e => e.trim()).filter(e => e) : [];
      const response = await bulkSendOfferLetters(selectedOffers, ccList);
      toast.success(`${response.data.sent} offer letters sent successfully`);
      setSelectedOffers([]);
      setBulkSendDialogOpen(false);
      setBulkSendCC('');
      fetchOffers();
    } catch (error) {
      toast.error('Failed to send offer letters');
    }
  };

  const openBulkSendDialog = () => {
    if (selectedOffers.length === 0) {
      toast.error('Please select at least one offer letter');
      return;
    }
    setBulkSendCC('');
    setBulkSendDialogOpen(true);
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await downloadOfferLetterTemplate();
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'offer_letter_template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleBulkDownload = async () => {
    if (selectedOffers.length === 0) {
      toast.error('Please select offer letters to download');
      return;
    }
    
    setBulkDownloading(true);
    try {
      const response = await bulkDownloadOfferLetters({ offer_ids: selectedOffers });
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `offer_letters_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`${selectedOffers.length} offer letters downloaded`);
      setSelectedOffers([]);
    } catch (error) {
      console.error('Bulk download error:', error);
      toast.error(error.response?.data?.detail || 'Failed to download offer letters');
    } finally {
      setBulkDownloading(false);
    }
  };

  const handleBulkUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }
    try {
      const ccList = uploadCC ? uploadCC.split(',').map(e => e.trim()).filter(e => e) : [];
      const response = await bulkUploadOfferLetters(uploadFile, ccList);
      toast.success(`${response.data.created} offer letters created`);
      if (response.data.errors.length > 0) {
        toast.warning(`${response.data.errors.length} errors occurred`);
      }
      setUploadDialogOpen(false);
      setUploadFile(null);
      setUploadCC('');
      fetchOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    }
  };

  const resetForm = () => {
    const defaultTemplate = templates.find(t => t.is_default);
    setFormData({
      template_id: defaultTemplate?.id || '',
      honorific: 'Mr.',
      employee_id: '',
      candidate_name: '',
      candidate_email: '',
      designation: '',
      work_location: '',
      department: '',
      offer_date: new Date().toISOString().split('T')[0],
      start_date: '',
      annexure_effective_date: '',
      basic_da: 17500,
      hra: 8750,
      statutory_bonus: 1750,
      other_allowance: 7000,
      variable_amount: 120000,
      pf_deduction: 0,
      esic_deduction: 0,
      lwf_deduction: 0,
      professional_tax: 200,
      insurance_deduction: 400,
      tds_deduction: null
    });
  };

  const handleTemplateChange = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    setFormData(prev => {
      const next = { ...prev, template_id: templateId };
      // Pre-fill TDS from the template's salary-structure default (fixed ₹
      // or % of gross). HR can still override per offer; clearing the field
      // reverts to the template default at creation time.
      const ss = template?.salary_structure;
      if (ss && (parseFloat(ss.tds_amount) > 0 || parseFloat(ss.tds_percentage) > 0)) {
        if (ss.tds_kind === 'percentage') {
          const gross = (prev.basic_da || 0) + (prev.hra || 0) + (prev.statutory_bonus || 0) + (prev.other_allowance || 0);
          next.tds_deduction = Math.round(gross * (parseFloat(ss.tds_percentage) || 0) / 100);
        } else {
          next.tds_deduction = parseFloat(ss.tds_amount) || 0;
        }
      }
      return next;
    });
    if (template?.salary_structure) {
      // Optionally update salary fields based on template structure
      toast.info(`Template "${template.name}" selected. Adjust salary values as needed.`);
    }
  };

  const toggleSelectOffer = (offerId) => {
    setSelectedOffers(prev =>
      prev.includes(offerId)
        ? prev.filter(id => id !== offerId)
        : [...prev, offerId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedOffers.length === offers.length) {
      setSelectedOffers([]);
    } else {
      setSelectedOffers(offers.map(o => o.id));
    }
  };

  const calculateSalary = () => {
    const gross = formData.basic_da + formData.hra + formData.statutory_bonus + formData.other_allowance;
    const deductions = formData.pf_deduction + formData.esic_deduction + formData.lwf_deduction + formData.professional_tax + formData.insurance_deduction + (formData.tds_deduction || 0);
    const net = gross - deductions;
    const ctc = gross * 12 + formData.variable_amount;
    return { gross, deductions, net, ctc };
  };

  const { gross, deductions, net, ctc } = calculateSalary();

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Offer Letters</h1>
            <p className="text-slate-600 mt-1">Generate and send bulk offer letters to candidates</p>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <AutoRefreshToggle
              inProgressCount={inProgressCount}
              onChanged={() => {
                fetchInProgressCount();
                fetchStaleCount();
                fetchOffers();
              }}
            />
            {inProgressCount > 0 && (
              <Button
                variant="outline"
                className="rounded-full border-cyan-300 bg-cyan-50 text-cyan-800 hover:bg-cyan-100"
                onClick={() => setRefreshDialogOpen(true)}
                data-testid="refresh-statuses-btn"
                title={`${inProgressCount} in-progress e-sign request(s). Click to poll Zoho for latest status.`}
              >
                <RefreshCw className="mr-2" size={18} />
                Refresh Status ({inProgressCount})
              </Button>
            )}
            {staleCount > 0 && (
              <Button
                variant="outline"
                className="rounded-full border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 animate-pulse-slow"
                onClick={() => setStaleDialogOpen(true)}
                data-testid="remind-stale-btn"
                title={`${staleCount} e-sign request(s) have had no activity for 24+ hours`}
              >
                <Bell className="mr-2" size={18} />
                Remind Stale ({staleCount})
              </Button>
            )}
            {/* Bulk Upload Dialog */}
            <Dialog open={uploadDialogOpen} onOpenChange={(open) => {
              setUploadDialogOpen(open);
              if (!open) {
                setUploadFile(null);
                setUploadCC('');
              }
            }}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full">
                  <Upload className="mr-2" size={18} />
                  Bulk Upload
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Bulk Upload Offer Letters</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <Button variant="outline" onClick={handleDownloadTemplate} className="w-full">
                    <FileSpreadsheet className="mr-2" size={18} />
                    Download Excel Template
                  </Button>
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => setUploadFile(e.target.files[0])}
                      className="hidden"
                      id="bulk-upload-file"
                    />
                    <label htmlFor="bulk-upload-file" className="cursor-pointer">
                      <Upload className="mx-auto h-10 w-10 text-slate-400 mb-2" />
                      <p className="text-sm text-slate-600">
                        {uploadFile ? uploadFile.name : 'Click to select Excel file'}
                      </p>
                    </label>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="upload_cc">CC for All Emails (Optional)</Label>
                    <Input
                      id="upload_cc"
                      type="text"
                      value={uploadCC}
                      onChange={(e) => setUploadCC(e.target.value)}
                      placeholder="hr@company.com, manager@company.com"
                    />
                    <p className="text-xs text-slate-500">These recipients will be CC&#39;d when offer letters are sent. Separate multiple emails with commas.</p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
                  <Button onClick={handleBulkUpload} disabled={!uploadFile} className="bg-indigo-600 hover:bg-indigo-700">
                    Upload & Create
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Create Single Offer Dialog */}
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full">
                  <Plus className="mr-2" size={18} />
                  Create Offer
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create New Offer Letter</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Template Selection */}
                  {templates.length > 0 && (
                    <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <Label htmlFor="template_id" className="text-indigo-900 font-medium">Select Template</Label>
                          <p className="text-xs text-indigo-600">Template determines terms & conditions in the offer letter</p>
                        </div>
                        <Link to="/offer-letter-templates" className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
                          <Settings2 size={12} />
                          Manage Templates
                        </Link>
                      </div>
                      <Select
                        value={formData.template_id}
                        onValueChange={handleTemplateChange}
                      >
                        <SelectTrigger className="mt-2 bg-white">
                          <SelectValue placeholder="Select a template" />
                        </SelectTrigger>
                        <SelectContent>
                          {templates.map(template => (
                            <SelectItem key={template.id} value={template.id}>
                              {template.name} {template.is_default && '(Default)'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  <Tabs defaultValue="candidate" className="w-full">
                    <TabsList className="grid w-full grid-cols-3">
                      <TabsTrigger value="candidate">Candidate Info</TabsTrigger>
                      <TabsTrigger value="salary">Salary Details</TabsTrigger>
                      <TabsTrigger value="deductions">Deductions</TabsTrigger>
                    </TabsList>

                    <TabsContent value="candidate" className="space-y-4 mt-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="honorific">Honorific *</Label>
                          <Select
                            value={formData.honorific}
                            onValueChange={(value) => setFormData({ ...formData, honorific: value })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Mr.">Mr.</SelectItem>
                              <SelectItem value="Ms.">Ms.</SelectItem>
                              <SelectItem value="Mrs.">Mrs.</SelectItem>
                              <SelectItem value="Dr.">Dr.</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="employee_id">Employee ID</Label>
                          <Input
                            id="employee_id"
                            value={formData.employee_id}
                            onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                            placeholder="e.g., EMP001"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="candidate_name">Candidate Name *</Label>
                          <Input
                            id="candidate_name"
                            value={formData.candidate_name}
                            onChange={(e) => setFormData({ ...formData, candidate_name: e.target.value })}
                            placeholder="Enter full name"
                            required
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="candidate_email">Email *</Label>
                          <Input
                            id="candidate_email"
                            type="email"
                            value={formData.candidate_email}
                            onChange={(e) => setFormData({ ...formData, candidate_email: e.target.value })}
                            placeholder="candidate@email.com"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="designation">Designation *</Label>
                          <Input
                            id="designation"
                            value={formData.designation}
                            onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                            placeholder="e.g., Team Lead"
                            required
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="work_location">Work Location *</Label>
                          <Input
                            id="work_location"
                            value={formData.work_location}
                            onChange={(e) => setFormData({ ...formData, work_location: e.target.value })}
                            placeholder="e.g., Delhi NCR"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="department">
                            Department
                            <span className="text-xs font-normal text-slate-500 ml-1">(optional)</span>
                          </Label>
                          <Input
                            id="department"
                            value={formData.department}
                            onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                            placeholder="e.g., Digital Consulting"
                            data-testid="department-input"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="offer_date">Offer Date *</Label>
                          <Input
                            id="offer_date"
                            type="date"
                            value={formData.offer_date}
                            onChange={(e) => setFormData({ ...formData, offer_date: e.target.value })}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="start_date">Start Date *</Label>
                          <Input
                            id="start_date"
                            type="date"
                            value={formData.start_date}
                            onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="annexure_effective_date">
                            Annexure Effective Date
                            <span className="text-xs font-normal text-slate-500 ml-1">(optional — defaults to Start Date)</span>
                          </Label>
                          <Input
                            id="annexure_effective_date"
                            type="date"
                            value={formData.annexure_effective_date}
                            onChange={(e) => setFormData({ ...formData, annexure_effective_date: e.target.value })}
                            data-testid="annexure-effective-date-input"
                          />
                          <p className="text-[11px] text-slate-500">
                            Shown on the Salary Annexure cover page. Set this if the annexure comes into force
                            on a different date than the Date of Joining (e.g. mid-cycle salary revision).
                          </p>
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="salary" className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="basic_da">Basic + DA (Per Month)</Label>
                          <Input
                            id="basic_da"
                            type="number"
                            value={formData.basic_da}
                            onChange={(e) => setFormData({ ...formData, basic_da: parseFloat(e.target.value) || 0 })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="hra">HRA (Per Month)</Label>
                          <Input
                            id="hra"
                            type="number"
                            value={formData.hra}
                            onChange={(e) => setFormData({ ...formData, hra: parseFloat(e.target.value) || 0 })}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="statutory_bonus">Statutory Bonus (Per Month)</Label>
                          <Input
                            id="statutory_bonus"
                            type="number"
                            value={formData.statutory_bonus}
                            onChange={(e) => setFormData({ ...formData, statutory_bonus: parseFloat(e.target.value) || 0 })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="other_allowance">Other Allowance (Per Month)</Label>
                          <Input
                            id="other_allowance"
                            type="number"
                            value={formData.other_allowance}
                            onChange={(e) => setFormData({ ...formData, other_allowance: parseFloat(e.target.value) || 0 })}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="variable_amount">Variable (Per Annum)</Label>
                        <Input
                          id="variable_amount"
                          type="number"
                          value={formData.variable_amount}
                          onChange={(e) => setFormData({ ...formData, variable_amount: parseFloat(e.target.value) || 0 })}
                        />
                      </div>

                      {/* Salary Summary */}
                      <Card className="bg-indigo-50 border-indigo-200">
                        <CardContent className="p-4">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-slate-600">Gross Salary (PM)</p>
                              <p className="text-xl font-bold text-indigo-700">₹{gross.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-slate-600">Total CTC (PA)</p>
                              <p className="text-xl font-bold text-indigo-700">₹{ctc.toLocaleString()}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="deductions" className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="pf_deduction">PF Deduction</Label>
                          <Input
                            id="pf_deduction"
                            type="number"
                            value={formData.pf_deduction}
                            onChange={(e) => setFormData({ ...formData, pf_deduction: parseFloat(e.target.value) || 0 })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="esic_deduction">ESIC Deduction</Label>
                          <Input
                            id="esic_deduction"
                            type="number"
                            value={formData.esic_deduction}
                            onChange={(e) => setFormData({ ...formData, esic_deduction: parseFloat(e.target.value) || 0 })}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="lwf_deduction">LWF Deduction</Label>
                          <Input
                            id="lwf_deduction"
                            type="number"
                            value={formData.lwf_deduction}
                            onChange={(e) => setFormData({ ...formData, lwf_deduction: parseFloat(e.target.value) || 0 })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="professional_tax">Professional Tax</Label>
                          <Input
                            id="professional_tax"
                            type="number"
                            value={formData.professional_tax}
                            onChange={(e) => setFormData({ ...formData, professional_tax: parseFloat(e.target.value) || 0 })}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="insurance_deduction">Insurance Deduction</Label>
                        <Input
                          id="insurance_deduction"
                          type="number"
                          value={formData.insurance_deduction}
                          onChange={(e) => setFormData({ ...formData, insurance_deduction: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="tds_deduction">TDS (Income Tax) / month</Label>
                        <Input
                          id="tds_deduction"
                          type="number"
                          placeholder="Template default if left empty"
                          value={formData.tds_deduction ?? ''}
                          onChange={(e) => setFormData({ ...formData, tds_deduction: e.target.value === '' ? null : (parseFloat(e.target.value) || 0) })}
                          data-testid="tds-deduction-input"
                        />
                        <p className="text-[11px] text-slate-500">Leave empty to use the selected template&apos;s TDS default.</p>
                      </div>

                      {/* Net Salary Summary */}
                      <Card className="bg-emerald-50 border-emerald-200">
                        <CardContent className="p-4">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-slate-600">Total Deductions (PM)</p>
                              <p className="text-xl font-bold text-red-600">₹{deductions.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-slate-600">Net Salary (In Hand)</p>
                              <p className="text-xl font-bold text-emerald-700">₹{net.toLocaleString()}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>
                  </Tabs>

                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => {
                      setDialogOpen(false);
                      resetForm();
                    }}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                      Create Offer Letter
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-indigo-100 text-xs">Total Offers</p>
                  <p className="text-2xl font-bold" data-testid="stats-total">{statsCounts.total}</p>
                </div>
                <FileText className="h-8 w-8 text-indigo-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-xs">Generated</p>
                  <p className="text-2xl font-bold" data-testid="stats-generated">{statsCounts.generated}</p>
                </div>
                <Clock className="h-8 w-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-xs">Sent</p>
                  <p className="text-2xl font-bold" data-testid="stats-sent">{(statsCounts.sent || 0) + (statsCounts.accepted || 0)}</p>
                </div>
                <Send className="h-8 w-8 text-emerald-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-xs">Accepted</p>
                  <p className="text-2xl font-bold" data-testid="stats-accepted">{statsCounts.accepted}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bulk Actions Bar */}
        {selectedOffers.length > 0 && (
          <Card className="bg-indigo-50 border-indigo-200">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users size={20} className="text-indigo-600" />
                <span className="font-medium text-indigo-900">{selectedOffers.length} offers selected</span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedOffers([])}
                >
                  Clear Selection
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkDownload}
                  disabled={bulkDownloading}
                  data-testid="bulk-download-btn"
                >
                  <Download className="mr-2" size={16} />
                  {bulkDownloading ? 'Downloading...' : 'Download Selected'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setBulkAnnexValue(''); setBulkAnnexDialogOpen(true); }}
                  data-testid="bulk-annex-date-btn"
                >
                  <CalendarClock className="mr-2" size={16} />
                  Set Effective Date
                </Button>
                <Button
                  size="sm"
                  className="bg-fuchsia-600 hover:bg-fuchsia-700"
                  onClick={() => {
                    if (selectedOffers.length === 0) {
                      toast.error('Please select at least one offer letter');
                      return;
                    }
                    setEsignBulkOpen(true);
                  }}
                  data-testid="bulk-esign-btn"
                >
                  <PenLine className="mr-2" size={16} />
                  Bulk E-Sign
                </Button>
                <Button
                  size="sm"
                  className="bg-indigo-600 hover:bg-indigo-700"
                  onClick={openBulkSendDialog}
                >
                  <Send className="mr-2" size={16} />
                  Send Selected
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Offer Letters Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <CardTitle>All Offer Letters</CardTitle>
                <CardDescription>View and manage offer letters</CardDescription>
              </div>
              <div className="relative w-full sm:w-96">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <Input
                  type="text"
                  placeholder="Search by name, email, ID, designation, location…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-9"
                  data-testid="offers-search-input"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                    data-testid="offers-search-clear"
                    title="Clear"
                  >
                    <XCircle size={14} />
                  </button>
                )}
              </div>
            </div>
            {(searchQuery || total > pageSize) && (
              <div className="text-xs text-slate-500 mt-2 flex items-center gap-2 flex-wrap">
                {total > 0 ? (
                  <span>
                    Showing <span className="font-semibold text-slate-700">{(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)}</span>
                    {' '}of <span className="font-semibold text-slate-700">{total}</span> offer{total === 1 ? '' : 's'}
                    {searchQuery && <> matching <span className="font-medium">&ldquo;{searchQuery}&rdquo;</span></>}
                  </span>
                ) : (
                  searchQuery && <span>No offer letters match <span className="font-medium">&ldquo;{searchQuery}&rdquo;</span>.</span>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent>
            {offers.length === 0 && !searchQuery ? (
              <div className="text-center py-12" data-testid="offers-empty-state">
                <Mail size={48} className="mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No offer letters created yet</p>
                <Button
                  className="mt-4 bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => setDialogOpen(true)}
                >
                  Create First Offer
                </Button>
              </div>
            ) : offers.length === 0 && searchQuery ? (
              <div className="text-center py-12" data-testid="offers-search-empty">
                <Search size={40} className="mx-auto text-slate-300 mb-3" />
                <p className="text-slate-600">
                  No offer letters matching <span className="font-medium">&ldquo;{searchQuery}&rdquo;</span>.
                </p>
                <button
                  type="button"
                  className="mt-3 underline text-indigo-600 hover:text-indigo-700 text-sm"
                  onClick={() => setSearchQuery('')}
                  data-testid="offers-clear-search"
                >
                  Clear search
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-3 font-medium">
                        <Checkbox
                          checked={selectedOffers.length === offers.length}
                          onCheckedChange={toggleSelectAll}
                        />
                      </th>
                      <th className="text-left p-3 font-medium">Emp ID</th>
                      <th className="text-left p-3 font-medium">Offer ID</th>
                      <th className="text-left p-3 font-medium">Candidate</th>
                      <th className="text-left p-3 font-medium">Designation</th>
                      <th className="text-left p-3 font-medium">Work Location</th>
                      <th className="text-left p-3 font-medium">CTC</th>
                      <th
                        className="text-left p-3 font-medium cursor-pointer select-none hover:bg-slate-100"
                        onClick={() => toggleSort('annexure_effective_date')}
                        data-testid="sort-annexure-effective-date"
                        title="Click to sort by Annexure Effective Date"
                      >
                        Effective Date
                        {sortField === 'annexure_effective_date' && (
                          <span className="ml-1 text-slate-500">
                            {sortDir === 'asc' ? '▲' : '▼'}
                          </span>
                        )}
                      </th>
                      <th className="text-left p-3 font-medium">Status</th>
                      <th className="text-left p-3 font-medium">E-Sign</th>
                      <th className="text-center p-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedOffers.map(offer => {
                      const StatusIcon = statusIcons[offer.status] || Clock;
                      return (
                        <tr key={offer.id} className="border-t hover:bg-slate-50">
                          <td className="p-3">
                            <Checkbox
                              checked={selectedOffers.includes(offer.id)}
                              onCheckedChange={() => toggleSelectOffer(offer.id)}
                            />
                          </td>
                          <td className="p-3">
                            <span className="font-mono text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                              {offer.employee_id || '-'}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className="font-mono text-xs bg-slate-100 px-2 py-1 rounded">
                              {offer.offer_id}
                            </span>
                          </td>
                          <td className="p-3">
                            <div>
                              <p className="font-medium text-slate-900">{offer.candidate_name}</p>
                              <p className="text-xs text-slate-500">{offer.candidate_email}</p>
                            </div>
                          </td>
                          <td className="p-3 text-slate-700">{offer.designation}</td>
                          <td className="p-3 text-slate-600">{offer.work_location || offer.client_location}</td>
                          <td className="p-3">
                            <span className="font-medium text-indigo-600">
                              ₹{(offer.total_ctc || 0).toLocaleString()}
                            </span>
                            <span className="text-xs text-slate-500"> /year</span>
                          </td>
                          <td className="p-3" data-testid={`effective-date-cell-${offer.id}`}>
                            {offer.annexure_effective_date ? (
                              <span className="text-xs font-medium text-teal-700 bg-teal-50 px-2 py-1 rounded">
                                {offer.annexure_effective_date}
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </td>
                          <td className="p-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusColors[offer.status]}`}>
                              <StatusIcon size={12} />
                              {offer.status.charAt(0).toUpperCase() + offer.status.slice(1)}
                            </span>
                          </td>
                          <td className="p-3">
                            {offer.esign?.status ? (() => {
                              const status = offer.esign.status;
                              const lastEventAt = offer.esign.last_event_at;
                              const isInProgress = status === 'SENT' || status === 'VIEWED';
                              let staleHours = 0;
                              if (lastEventAt) {
                                try { staleHours = (Date.now() - new Date(lastEventAt).getTime()) / 3_600_000; } catch { /* ignore */ }
                              }
                              const isStale = isInProgress && staleHours > 24;
                              const days = Math.floor(staleHours / 24);
                              return (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setHistoryOffer(offer);
                                    setHistoryInitialFilter('esign');
                                    setHistoryDialogOpen(true);
                                  }}
                                  title={
                                    isStale
                                      ? `Stale: ${status} since ${days}d ago — consider sending a reminder`
                                      : 'View e-sign timeline'
                                  }
                                  className={`relative inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold border transition hover:shadow-sm hover:scale-105 active:scale-100 ${
                                    status === 'SIGNED'
                                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                      : status === 'SENT' || status === 'VIEWED'
                                      ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                                      : status === 'DECLINED' || status === 'EXPIRED'
                                      ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                                      : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                                  } ${
                                    isStale
                                      ? 'ring-2 ring-amber-400 ring-offset-1 animate-pulse-slow'
                                      : ''
                                  }`}
                                  data-testid={`esign-status-${offer.id}`}
                                  data-stale={isStale ? 'true' : 'false'}
                                >
                                  {isStale && (
                                    <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
                                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500"></span>
                                    </span>
                                  )}
                                  {status}
                                </button>
                              );
                            })() : (
                              <span className="text-[10px] text-slate-400">—</span>
                            )}
                          </td>
                          <td className="p-3">
                            <div className="flex justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-blue-600 hover:text-blue-700"
                                onClick={() => handlePreviewPDF(offer)}
                                title="Preview PDF"
                                data-testid={`preview-pdf-${offer.id}`}
                              >
                                <Eye size={14} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleDownloadPDF(offer.id, offer.candidate_name)}
                                title="Download PDF"
                              >
                                <Download size={14} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-indigo-600 hover:text-indigo-700"
                                onClick={() => handleDownloadAnnexure(offer.id, offer.candidate_name)}
                                title="Download Annexure (for e-signing)"
                                data-testid={`download-annexure-${offer.id}`}
                              >
                                <Paperclip size={14} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-violet-600 hover:text-violet-700"
                                onClick={() => handleSendAnnexure(offer)}
                                title="Email Annexure to Candidate"
                                data-testid={`send-annexure-${offer.id}`}
                              >
                                <FileSignature size={14} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-teal-600 hover:text-teal-700"
                                onClick={() => openAnnexDateDialog(offer)}
                                title={
                                  offer.annexure_effective_date
                                    ? `Edit Annexure Effective Date (${offer.annexure_effective_date})`
                                    : 'Set Annexure Effective Date (defaults to DOJ)'
                                }
                                data-testid={`edit-annexure-date-${offer.id}`}
                              >
                                <CalendarClock size={14} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className={
                                  offer.status === 'accepted'
                                    ? 'h-8 w-8 text-emerald-600'
                                    : 'h-8 w-8 text-slate-500 hover:text-emerald-600'
                                }
                                onClick={() => handleAcceptOffer(offer)}
                                title={
                                  offer.status === 'accepted'
                                    ? `Accepted · BGV ${offer.bgv_case_number || '(pending)'}`
                                    : 'Mark accepted & auto-fire BGV case'
                                }
                                data-testid={`accept-offer-${offer.id}`}
                              >
                                <CheckCircle size={14} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-fuchsia-600 hover:text-fuchsia-700"
                                onClick={() => { setEsignOffer(offer); setEsignDialogOpen(true); }}
                                title="E-Sign Annexure (Zoho Sign)"
                                data-testid={`esign-annexure-${offer.id}`}
                              >
                                <PenLine size={14} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-amber-600 hover:text-amber-700"
                                onClick={() => {
                                  setHistoryOffer(offer);
                                  setHistoryInitialFilter('all');
                                  setHistoryDialogOpen(true);
                                }}
                                title="Annexure History"
                                data-testid={`annexure-history-${offer.id}`}
                              >
                                <History size={14} />
                              </Button>
                              {offer.status !== 'sent' && offer.status !== 'accepted' && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-emerald-600 hover:text-emerald-700"
                                  onClick={() => openSendDialog(offer)}
                                  title="Send Offer"
                                >
                                  <Send size={14} />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination Controls */}
            {total > 0 && (
              <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t flex-wrap">
                <div className="flex items-center gap-2 text-xs text-slate-600">
                  <span>Rows per page:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                    className="border rounded px-2 py-1 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    data-testid="page-size-select"
                  >
                    {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                  <span className="ml-2">
                    Page <strong>{page}</strong> of <strong>{Math.max(1, Math.ceil(total / pageSize))}</strong>
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(1)}
                    disabled={page <= 1}
                    data-testid="page-first"
                  >
                    « First
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    data-testid="page-prev"
                  >
                    ‹ Prev
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(Math.ceil(total / pageSize), p + 1))}
                    disabled={page >= Math.ceil(total / pageSize)}
                    data-testid="page-next"
                  >
                    Next ›
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(Math.max(1, Math.ceil(total / pageSize)))}
                    disabled={page >= Math.ceil(total / pageSize)}
                    data-testid="page-last"
                  >
                    Last »
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Send Email Dialog */}
        <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send size={20} className="text-emerald-600" />
                Send Offer Letter
              </DialogTitle>
            </DialogHeader>
            {selectedOfferToSend && (
              <div className="space-y-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600">Sending offer to:</p>
                  <p className="font-medium">{selectedOfferToSend.candidate_name}</p>
                  <p className="text-sm text-slate-500">{selectedOfferToSend.designation}</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="send_email">Recipient Email *</Label>
                  <Input
                    id="send_email"
                    type="email"
                    value={sendFormData.email}
                    onChange={(e) => setSendFormData({ ...sendFormData, email: e.target.value })}
                    placeholder="candidate@email.com"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="send_cc">CC (Optional)</Label>
                  <Input
                    id="send_cc"
                    type="text"
                    value={sendFormData.cc}
                    onChange={(e) => setSendFormData({ ...sendFormData, cc: e.target.value })}
                    placeholder="hr@company.com, manager@company.com"
                  />
                  <p className="text-xs text-slate-500">Separate multiple emails with commas</p>
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setSendDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => handleSendOffer(selectedOfferToSend.id, sendFormData.email, sendFormData.cc)}
                  >
                    <Send size={16} className="mr-2" />
                    Send Email
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Bulk Send Email Dialog */}
        <Dialog open={bulkSendDialogOpen} onOpenChange={setBulkSendDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send size={20} className="text-indigo-600" />
                Bulk Send Offer Letters
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-3 bg-indigo-50 rounded-lg">
                <p className="text-sm text-indigo-600">Sending <strong>{selectedOffers.length}</strong> offer letter(s)</p>
                <p className="text-xs text-indigo-500 mt-1">Each candidate will receive their respective offer letter</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="bulk_cc">CC for All Emails (Optional)</Label>
                <Input
                  id="bulk_cc"
                  type="text"
                  value={bulkSendCC}
                  onChange={(e) => setBulkSendCC(e.target.value)}
                  placeholder="hr@company.com, manager@company.com"
                />
                <p className="text-xs text-slate-500">These recipients will be CC&#39;d on all {selectedOffers.length} emails. Separate multiple emails with commas.</p>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setBulkSendDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  className="bg-indigo-600 hover:bg-indigo-700"
                  onClick={handleBulkSend}
                >
                  <Send size={16} className="mr-2" />
                  Send {selectedOffers.length} Email(s)
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>

        {/* PDF Preview Modal */}
        <Dialog open={previewOpen} onOpenChange={(open) => { if (!open) closePreview(); }}>
          <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0">
            <DialogHeader className="px-6 pt-5 pb-3 border-b shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle className="flex items-center gap-2" data-testid="preview-modal-title">
                    <Eye size={20} className="text-blue-600" />
                    {previewOffer ? `${previewOffer.candidate_name} — ${previewOffer.designation}` : 'PDF Preview'}
                  </DialogTitle>
                  <p className="text-xs text-slate-400 mt-1">Offer letter preview</p>
                </div>
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="preview-download-btn"
                  onClick={() => previewOffer && handleDownloadPDF(previewOffer.id, previewOffer.candidate_name)}
                >
                  <Download size={14} className="mr-1.5" />
                  Download
                </Button>
              </div>
            </DialogHeader>
            <div className="flex-1 min-h-0 bg-slate-100">
              {previewLoading ? (
                <div className="flex items-center justify-center h-full" data-testid="preview-loading">
                  <div className="text-center space-y-3">
                    <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-600 border-t-transparent mx-auto" />
                    <p className="text-sm text-slate-500">Generating preview...</p>
                  </div>
                </div>
              ) : previewUrl ? (
                <iframe
                  src={`${previewUrl}#toolbar=1&navpanes=0`}
                  className="w-full h-full border-0"
                  title="Offer Letter Preview"
                  data-testid="preview-iframe"
                />
              ) : null}
            </div>
          </DialogContent>
        </Dialog>

        <ZohoSignDialog
          open={esignDialogOpen}
          onOpenChange={(open) => { setEsignDialogOpen(open); if (!open) setEsignOffer(null); }}
          offer={esignOffer}
        />

        <ZohoSignBulkDialog
          open={esignBulkOpen}
          onOpenChange={setEsignBulkOpen}
          offers={offers.filter((o) => selectedOffers.includes(o.id))}
          onComplete={(res) => {
            if (res?.sent > 0) {
              setSelectedOffers([]);
              fetchOffers();
            }
          }}
        />

        <AnnexureHistoryDialog
          open={historyDialogOpen}
          onOpenChange={(open) => { setHistoryDialogOpen(open); if (!open) setHistoryOffer(null); }}
          offer={historyOffer}
          initialFilter={historyInitialFilter}
        />

        <ZohoSignRemindStaleDialog
          open={staleDialogOpen}
          onOpenChange={setStaleDialogOpen}
          onComplete={() => { fetchStaleCount(); fetchOffers(); }}
        />

        <ZohoSignBulkRefreshDialog
          open={refreshDialogOpen}
          onOpenChange={setRefreshDialogOpen}
          inProgressCount={inProgressCount}
          onComplete={() => {
            fetchInProgressCount();
            fetchStaleCount();
            fetchOffers();
          }}
        />

        <Dialog
          open={annexDateDialogOpen}
          onOpenChange={(open) => {
            setAnnexDateDialogOpen(open);
            if (!open) { setAnnexDateOffer(null); setAnnexDateValue(''); }
          }}
        >
          <DialogContent className="max-w-md" data-testid="annexure-date-dialog">
            <DialogHeader>
              <DialogTitle>Annexure Effective Date</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {annexDateOffer && (
                <div className="text-sm text-slate-600">
                  <p><strong>Candidate:</strong> {annexDateOffer.candidate_name}</p>
                  <p><strong>Date of Joining:</strong> {annexDateOffer.date_of_joining || annexDateOffer.start_date || '—'}</p>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="annex-date-edit">Effective Date</Label>
                <Input
                  id="annex-date-edit"
                  type="date"
                  value={annexDateValue}
                  onChange={(e) => setAnnexDateValue(e.target.value)}
                  data-testid="annexure-date-edit-input"
                />
                <p className="text-[11px] text-slate-500">
                  Shown on the Salary Annexure cover page. Leave empty to clear the
                  override — the PDF will fall back to the Date of Joining.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => { setAnnexDateValue(''); }}
                data-testid="annexure-date-clear-btn"
              >
                Clear
              </Button>
              <Button
                onClick={handleSaveAnnexDate}
                disabled={annexDateSaving}
                data-testid="annexure-date-save-btn"
              >
                {annexDateSaving ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={bulkAnnexDialogOpen}
          onOpenChange={(open) => {
            setBulkAnnexDialogOpen(open);
            if (!open) setBulkAnnexValue('');
          }}
        >
          <DialogContent className="max-w-md" data-testid="bulk-annexure-date-dialog">
            <DialogHeader>
              <DialogTitle>Bulk Set Annexure Effective Date</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="rounded-md bg-indigo-50 border border-indigo-200 p-3 text-sm">
                <p className="text-indigo-900">
                  <strong>{selectedOffers.length}</strong> offer{selectedOffers.length === 1 ? '' : 's'} selected.
                </p>
                <p className="text-indigo-700 text-xs mt-1">
                  Use this during salary revision cycles to apply the same effective date across many
                  annexures at once.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="bulk-annex-date">Effective Date</Label>
                <Input
                  id="bulk-annex-date"
                  type="date"
                  value={bulkAnnexValue}
                  onChange={(e) => setBulkAnnexValue(e.target.value)}
                  data-testid="bulk-annexure-date-input"
                />
                <p className="text-[11px] text-slate-500">
                  Leave empty and click <em>Clear on All</em> to remove the override on the selected offers
                  — their PDFs will fall back to each offer&apos;s Date of Joining.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => { setBulkAnnexValue(''); handleBulkSaveAnnexDate(); }}
                disabled={bulkAnnexSaving}
                data-testid="bulk-annexure-date-clear-btn"
              >
                Clear on All
              </Button>
              <Button
                onClick={handleBulkSaveAnnexDate}
                disabled={bulkAnnexSaving || !bulkAnnexValue}
                data-testid="bulk-annexure-date-save-btn"
              >
                {bulkAnnexSaving ? 'Saving…' : `Apply to ${selectedOffers.length}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog
          open={acceptDialogOpen}
          onOpenChange={(o) => { setAcceptDialogOpen(o); if (!o) setAcceptOffer(null); }}
        >
          <DialogContent className="max-w-md" data-testid="accept-offer-dialog">
            <DialogHeader>
              <DialogTitle>Accept Offer & Background Verification</DialogTitle>
            </DialogHeader>
            {acceptOffer && (
              <div className="space-y-4">
                <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-sm">
                  <p><strong>{acceptOffer.candidate_name}</strong></p>
                  <p className="text-slate-500 text-xs">{acceptOffer.candidate_email}</p>
                  <p className="text-slate-500 text-xs">
                    DOJ: {acceptOffer.start_date || acceptOffer.date_of_joining || '—'}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>When should BGV run?</Label>
                  <div className="space-y-2">
                    {[
                      { v: 'now',       t: 'Run BGV now',              d: 'Email portal link to candidate immediately' },
                      { v: 'scheduled', t: 'Schedule for after joining', d: 'Auto-fire BGV on the DOJ (or a custom date)' },
                      { v: 'skip',      t: 'Skip BGV for this offer',   d: 'No BGV case will be created' },
                    ].map(opt => (
                      <label key={opt.v}
                        className={
                          'block rounded-md border p-3 cursor-pointer transition-colors ' +
                          (acceptTrigger === opt.v
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-slate-200 hover:border-slate-300')
                        }
                        data-testid={`bgv-trigger-${opt.v}`}
                      >
                        <div className="flex items-start gap-2">
                          <input
                            type="radio"
                            name="bgv-trigger"
                            value={opt.v}
                            checked={acceptTrigger === opt.v}
                            onChange={() => setAcceptTrigger(opt.v)}
                            className="mt-1"
                          />
                          <div>
                            <p className="font-medium text-sm">{opt.t}</p>
                            <p className="text-xs text-slate-500">{opt.d}</p>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                {acceptTrigger === 'scheduled' && (
                  <div className="space-y-2">
                    <Label>Fire on / after</Label>
                    <Input
                      type="date"
                      value={acceptScheduledAt}
                      onChange={(e) => setAcceptScheduledAt(e.target.value)}
                      data-testid="bgv-scheduled-date-input"
                    />
                    <p className="text-[11px] text-slate-500">
                      Defaults to the DOJ. HR (or a daily job) runs&nbsp;
                      <code>POST /api/bgv/process-scheduled</code> to fire all past-due cases.
                    </p>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setAcceptDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={confirmAcceptOffer}
                disabled={accepting || (acceptTrigger === 'scheduled' && !acceptScheduledAt)}
                data-testid="confirm-accept-btn"
              >
                {accepting ? 'Working…' : 'Accept Offer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default OfferLetters;
